/**
 * metaLeadGen/creativeBreakdown.ts
 *
 * Aggregates Meta rows by creative type and derives full webinar-funnel metrics.
 *
 * ARCHITECTURE CONTRACT:
 *   - Pure data layer. No UI imports.
 *   - Creative type is derived from ad name via classifyCreativeType().
 *   - Meta fields (spend, impressions, clicks, leads, lpv) are real aggregates.
 *   - Applications and closed use real Close CRM attribution when available,
 *     keyed by ad name (lowercased). Falls back to mock rates if data is missing.
 *   - All rate/cost fields are 0 when denominator is 0 (no data for that type).
 *
 * Opt-in Rate formula (per spec): Opt-ins / Clicks   (not / LPV)
 */

import type { MetaLeadGenRow, MetaLeadGenFilters } from "./types";
import type { CreativeType } from "./types";
import { classifyCreativeType, ALL_CREATIVE_TYPES } from "./creativeType";
import { applyFilters } from "./filters";
import type { CloseCreativeMap } from "./useCloseCreativeAttribution";
import { safeDivide } from "./calculations";
import { branding } from "../../../config/branding";

// ─────────────────────────────────────────────
// COLORS — one per creative type, from branding palette
// ─────────────────────────────────────────────
const C = branding.colors;

export const CREATIVE_COLORS: Record<CreativeType, string> = {
  "Video":        C.chart[1],  // indigo/purple
  "Static Image": C.chart[3],  // blue
  "Carousel":     C.chart[4],  // violet
  "UGC":          C.chart[0],  // cyan/teal
  "Other":        C.chart[2],  // lavender (muted / last resort)
};

export const CREATIVE_LABELS: Record<CreativeType, string> = {
  "Video":        "Video",
  "Static Image": "Static",
  "Carousel":     "Carousel",
  "UGC":          "Influencer / UGC",
  "Other":        "Other",
};

// ─────────────────────────────────────────────
// MOCK CRM CONVERSION RATES BY CREATIVE TYPE
// Replace with live Close CRM data when available.
// ─────────────────────────────────────────────
const MOCK_APP_RATES: Record<CreativeType, number> = {
  "Video":        0.092,
  "Static Image": 0.078,
  "Carousel":     0.065,
  "UGC":          0.105,
  "Other":        0.055,
};

const MOCK_CLOSE_RATES: Record<CreativeType, number> = {
  "Video":        0.245,
  "Static Image": 0.220,
  "Carousel":     0.195,
  "UGC":          0.262,
  "Other":        0.180,
};

// ─────────────────────────────────────────────
// OUTPUT TYPE
// ─────────────────────────────────────────────
export interface CreativeFunnelRow {
  type:         CreativeType;
  label:        string;        // display label e.g. "Influencer / UGC"
  color:        string;        // hex from branding palette

  // Raw volume
  spend:        number;
  impressions:  number;
  clicks:       number;
  optins:       number;        // leads from Meta
  applications: number;        // mock-derived from optins × MOCK_APP_RATES
  closed:       number;        // mock-derived from applications × MOCK_CLOSE_RATES

  // Stage-to-stage conversion rates (%)
  ctr:          number;        // clicks / impressions × 100
  optinRate:    number;        // optins / clicks × 100        (per spec: /clicks not /lpv)
  appRate:      number;        // applications / optins × 100
  closeRate:    number;        // closed / applications × 100

  // Cost efficiency
  costPerOptin: number;        // spend / optins  (0 if no optins)
  costPerApp:   number;        // spend / applications (0 if no apps)
}

// ─────────────────────────────────────────────
// PUBLIC API
// ─────────────────────────────────────────────

/**
 * Returns one CreativeFunnelRow per creative type in ALL_CREATIVE_TYPES order.
 * Types with zero impressions are included (all metrics = 0) for consistent layout.
 *
 * closeAttribution — optional map from Close CRM (ad name lowercase → stats).
 * When provided, applications/closed are real Close CRM numbers. Falls back to
 * mock rates for any type with no matching Close data.
 */
export function getCreativeFunnel(
  rows: MetaLeadGenRow[],
  filters: MetaLeadGenFilters,
  closeAttribution?: CloseCreativeMap
): CreativeFunnelRow[] {
  const filtered = applyFilters(rows, filters);

  const agg = new Map<CreativeType, { spend: number; impressions: number; clicks: number; leads: number }>();
  for (const t of ALL_CREATIVE_TYPES) {
    agg.set(t, { spend: 0, impressions: 0, clicks: 0, leads: 0 });
  }

  const adNamesByType = new Map<CreativeType, Set<string>>();
  for (const t of ALL_CREATIVE_TYPES) adNamesByType.set(t, new Set());

  for (const row of filtered) {
    const type = classifyCreativeType(row.adName);
    const acc  = agg.get(type)!;
    acc.spend       += row.spend;
    acc.impressions += row.impressions;
    acc.clicks      += row.clicks;
    acc.leads       += row.leads;
    if (row.adName) adNamesByType.get(type)!.add(row.adName.toLowerCase().trim());
  }

  return ALL_CREATIVE_TYPES.map(type => {
    const { spend, impressions, clicks, leads: optins } = agg.get(type)!;

    let applications: number;
    let closed: number;

    if (closeAttribution && closeAttribution.size > 0) {
      let realApps = 0;
      let realClosed = 0;
      for (const adName of adNamesByType.get(type)!) {
        const cr = closeAttribution.get(adName);
        if (cr) { realApps += cr.applications; realClosed += cr.closed; }
      }
      if (realApps > 0 || realClosed > 0) {
        applications = realApps;
        closed       = realClosed;
      } else {
        applications = Math.round(optins * MOCK_APP_RATES[type]);
        closed       = Math.round(applications * MOCK_CLOSE_RATES[type]);
      }
    } else {
      applications = Math.round(optins * MOCK_APP_RATES[type]);
      closed       = Math.round(applications * MOCK_CLOSE_RATES[type]);
    }

    return {
      type,
      label:        CREATIVE_LABELS[type],
      color:        CREATIVE_COLORS[type],
      spend,
      impressions,
      clicks,
      optins,
      applications,
      closed,
      ctr:          safeDivide(clicks,       impressions)  * 100,
      optinRate:    safeDivide(optins,        clicks)       * 100,
      appRate:      safeDivide(applications,  optins)       * 100,
      closeRate:    safeDivide(closed,        applications) * 100,
      costPerOptin: safeDivide(spend, optins),
      costPerApp:   safeDivide(spend, applications),
    };
  });
}
